<script setup lang="ts">
import { computed, defineAsyncComponent } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { useUIStore } from '@/stores/ui'
import { useAssistantShell } from '@/composables/useAssistantShell'
import Sidebar from '@/components/layout/Sidebar.vue'

const { t } = useI18n()
const uiStore = useUIStore()
const route = useRoute()
const isWideList = computed(() => ['contacts', 'leads', 'deals', 'catalog-orders', 'forms', 'inbox'].includes(String(route.name)))
// Pages whose main view grows to the bottom of the screen (board, calendar grid).
const isFullHeight = computed(() => ['deals', 'calendar'].includes(String(route.name)))
const DevPlanSwitcher = import.meta.env.DEV
  ? defineAsyncComponent(() => import('@/components/layout/DevPlanSwitcher.vue'))
  : null

const mainStyle = computed(() => ({
  // The store gives a pinned sidebar room in the page; an unpinned
  // compact sidebar expands over the content on hover.
  marginLeft: `${uiStore.contentMarginLeft}px`
}))

// Shared dock flag; shell/card look lives in assets/styles/assistant-shell.css.
const { encapsulated } = useAssistantShell()

const handleOverlayClick = () => {
  uiStore.closeMobileSidebar()
}

const handleMenuClick = () => {
  uiStore.toggleSidebar()
}
</script>

<template>
  <div
    class="app-shell assistant-shell assistant-shell--scroll"
    :class="{ 'assistant-shell--encapsulated': encapsulated }"
  >
    <div class="app-layout assistant-card">
      <!-- Sidebar -->
      <Sidebar :class="{ open: uiStore.mobileSidebarOpen }" />

      <!-- Main area -->
      <div class="app-main" :style="mainStyle">
        <component :is="DevPlanSwitcher" v-if="DevPlanSwitcher" />
        <!-- Mobile menu button -->
        <button
          v-if="uiStore.isMobile"
          class="mobile-menu-button"
          @click="handleMenuClick"
          :aria-label="t('layout.toggleMenu')"
        >
          <img src="/logo-short.svg" :alt="t('layout.menuAlt')" class="mobile-menu-logo" />
        </button>

        <!-- Scrollable content -->
        <main class="app-content">
          <div class="content-container" :class="{ 'content-container--wide': isWideList, 'content-container--fill': isFullHeight }">
            <div class="content-inner">
              <slot />
            </div>
          </div>
        </main>
      </div>

      <!-- Mobile overlay -->
      <Transition name="fade">
        <div
          v-if="uiStore.isMobile && uiStore.mobileSidebarOpen"
          class="mobile-overlay"
          @click="handleOverlayClick"
        />
      </Transition>
    </div>
  </div>
</template>

<style scoped>
/* Shell + inset-card look comes from the shared assistant-shell.css
   (.assistant-shell / .assistant-card classes on the same elements). Only
   AppLayout-specific layout stays below. */
.app-layout {
  display: flex;
  background-color: var(--color-background, #FFFFFF);
}

/* Sidebar transitions live on the base selector so they apply both when the
   encapsulated modifier is added AND when it's removed (otherwise close-time
   snaps back instantly because the override rule no longer applies). */
.app-shell :deep(.sidebar) {
  transition:
    width 0.3s ease,
    top 300ms ease,
    left 300ms ease,
    height 300ms ease,
    border-radius 300ms ease,
    border-right-color 300ms ease;
}

/* Re-align the fixed Sidebar so its left/top edges sit on the inset card.
   We keep the sidebar's own border-right (#D6D6D6) — the same medium-grey
   token now used on the card's outer border, so the divider matches. */
.assistant-shell--encapsulated :deep(.sidebar) {
  top: 17px;
  left: 17px;
  height: calc(100vh - 34px);
  border-radius: 12px 0 0 12px;
}

.app-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  min-width: 0; /* let wide rows (tabs, chips) scroll inside the page instead of widening it */
  transition: margin-left var(--transition-normal, 200ms ease);
}

.assistant-shell--encapsulated .app-main {
  /* Inner scroll container — overrides the default min-height: 100vh that
     would otherwise force document-level scroll. */
  height: 100%;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
}

.app-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  padding: 24px 0;
}

.content-container {
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 30px;
  display: flex;
  flex-direction: column;
}

/* List pages use the available width for their tables and boards. */
.content-container--wide {
  max-width: none;
}

.content-container--fill {
  flex: 1 0 auto;
  min-height: calc(100vh - 48px);
}

.assistant-shell--encapsulated .content-container--fill {
  min-height: calc(100vh - 82px);
}

.content-container--fill .content-inner {
  flex: 1 0 auto;
}

.content-inner {
  width: 100%;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  gap: 30px;
}

/* Mobile menu button */
.mobile-menu-button {
  position: fixed;
  top: 12px;
  left: 12px;
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  padding: 0;
  background: #FFFFFF;
  border: 1px solid #D6D6D6;
  border-radius: 10px;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
}

.mobile-menu-button:hover {
  background-color: #F9F9F9;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.mobile-menu-logo {
  height: 28px;
  width: auto;
}

/* Mobile overlay */
.mobile-overlay {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.1);
  z-index: calc(var(--z-fixed, 300) - 1);
}

/* Fade transition for overlay */
.fade-enter-active,
.fade-leave-active {
  transition: opacity var(--transition-normal, 200ms ease);
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Tablet page padding; the store controls sidebar spacing. */
@media (max-width: 1024px) {
  .content-container {
    padding: 0 24px;
  }
}

/* Mobile: No margin */
@media (max-width: 768px) {
  .app-main {
    margin-left: 0 !important;
  }

  .content-container {
    padding: 0 16px;
  }

  .app-content {
    padding: 16px 0;
    padding-top: 84px;
  }

  .content-inner {
    gap: 20px;
  }
}
</style>
